import React, { useState } from "react";
import { Button, MessageBar, MessageBarBody, Spinner, Table, TableBody, TableCell, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Text } from "@fluentui/react-components";
import { ArrowDownload20Regular, People20Regular } from "@fluentui/react-icons";
import { api } from "../spurt/http";
import { errText } from "./meetingErrors";
import { fetchBlob } from "./meetingFiles";
import { fmtClockTime, fmtDuration, safeFileName, saveBlob } from "./meetingFormat";
import css from "./Meetings.module.css";

interface AttendanceRow {
  userId: string;
  name: string;
  role?: string;
  guest?: boolean;
  firstJoinMs: number;
  lastLeaveMs: number;
  totalMs: number;
  sessions: number;
  percent: number;
  invited: boolean;
}
interface AttendanceOut {
  meetingId: string;
  title?: string;
  startedAtMs?: number;
  endedAtMs?: number;
  durationMs?: number;
  items: AttendanceRow[];
  noShows?: string[];
}

export interface AttendanceSectionProps {
  meetingId: string;
  title: string;
  nameOf: (userId: string) => string;
}

/** Host only: who came, when, and for how long. The CSV is fetched with the bearer token and saved as a file. */
const AttendanceSection: React.FC<AttendanceSectionProps> = ({ meetingId, title, nameOf }) => {
  const [data, setData] = useState<AttendanceOut | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const path = `/api/v1/meetings/${encodeURIComponent(meetingId)}/attendance`;

  const load = async () => {
    setBusy(true);
    setError("");
    try {
      setData(await api<AttendanceOut>(path));
    } catch (e) {
      setError(errText(e, "see attendance"));
    } finally {
      setBusy(false);
    }
  };

  const csv = async () => {
    setError("");
    try {
      saveBlob(`${safeFileName(title)}-attendance.csv`, await fetchBlob(path + ".csv"));
    } catch (e) {
      setError(errText(e, "download attendance"));
    }
  };

  return (
    <section className={css.section} aria-label="Attendance">
      <h3 className={css.h3}>Attendance</h3>
      {!data && (
        <div className={css.row}>
          <Button icon={busy ? <Spinner size="tiny" /> : <People20Regular />} disabled={busy} onClick={() => void load()}>
            Show attendance
          </Button>
        </div>
      )}
      {error && (
        <MessageBar intent="error" role="alert">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {data && (
        <>
          {data.items.length === 0 ? (
            <Text className={css.muted}>Nobody joined this meeting.</Text>
          ) : (
            <div className={css.tableWrap}>
              <Table size="small" aria-label="Attendance">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>First joined</TableHeaderCell>
                    <TableHeaderCell>Time in meeting</TableHeaderCell>
                    <TableHeaderCell>Share</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((r) => (
                    <TableRow key={r.userId}>
                      <TableCell>
                        <TableCellLayout>
                          {r.name || nameOf(r.userId)}
                          {r.guest ? " (guest)" : ""}
                          {!r.invited ? " (not invited)" : ""}
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>{fmtClockTime(r.firstJoinMs)}</TableCell>
                      <TableCell>{fmtDuration(r.totalMs)}</TableCell>
                      <TableCell>{Math.round(r.percent)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {(data.noShows ?? []).length > 0 && (
            <Text className={css.muted}>Did not join: {(data.noShows ?? []).map(nameOf).join(", ")}</Text>
          )}
          <div className={css.row}>
            <Button icon={<ArrowDownload20Regular />} onClick={() => void csv()}>
              Download CSV
            </Button>
            <Button onClick={() => void load()} disabled={busy}>
              Refresh
            </Button>
          </div>
        </>
      )}
    </section>
  );
};

export default AttendanceSection;
